import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import backIcon from "../assets/ecommerce/atras.png";
import { useMedioMayoristaCart } from "../context/MedioMayoristaCartContext";
import OtherProductsCarousel from "../components/Ecommerce/OtherProductsCarousel";
import {
  fetchMedioMayoristaProducts,
  toCarouselItem,
} from "../components/Ecommerce/medioMayoristaCatalog";

const formatCurrency = new Intl.NumberFormat("es-MX", {
  style: "currency",
  currency: "MXN",
  minimumFractionDigits: 2,
});

const parseNumber = (value, fallback = 0) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const normalizeString = (value) => {
  if (value === undefined || value === null) return null;
  const trimmed = String(value).trim();
  return trimmed.length ? trimmed : null;
};

const isRn1Name = (name) =>
  (normalizeString(name) || "").toLowerCase().includes("rn1");
const isRecommendedResin = (name) =>
  (normalizeString(name) || "").toLowerCase().includes("uv");

const MedioMayoristaKitFiberResinDetail = () => {
  const { addItem, openCart, items } = useMedioMayoristaCart();
  const [fiberOptions, setFiberOptions] = useState([]);
  const [resinOptions, setResinOptions] = useState([]);
  const [selectedFiberId, setSelectedFiberId] = useState(null);
  const [selectedResinId, setSelectedResinId] = useState(null);
  const [includeRn1, setIncludeRn1] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeImage, setActiveImage] = useState("");
  const [otherProducts, setOtherProducts] = useState([]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");

    Promise.all([
      fetchMedioMayoristaProducts("fibra", "No se pudieron cargar las fibras para el kit."),
      fetchMedioMayoristaProducts("resina", "No se pudieron cargar las resinas para el kit."),
    ])
      .then(([fibersData, resinsData]) => {
        if (!active) return;

        const kitFibers = (Array.isArray(fibersData) ? fibersData : []).filter(
          (fiber) => !!fiber?.kit
        );
        const kitResins = (Array.isArray(resinsData) ? resinsData : []).filter(
          (resin) => !!resin?.kit
        );

        if (!kitFibers.length) {
          throw new Error("No hay fibras habilitadas para kit.");
        }
        if (!kitResins.length) {
          throw new Error("No hay resinas habilitadas para kit.");
        }

        setFiberOptions(kitFibers);
        setResinOptions(kitResins);
        setOtherProducts((Array.isArray(fibersData) ? fibersData : []).map((fiber) => toCarouselItem(fiber, "fibra")));
      })
      .catch((fetchError) => {
        if (!active) return;
        setError(fetchError.message || "No se pudo cargar la configuración del kit.");
        setOtherProducts([]);
      })
      .finally(() => {
        if (!active) return;
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const rn1Resin = useMemo(
    () =>
      resinOptions.find((resin) => isRn1Name(normalizeString(resin?.name) || "")) || null,
    [resinOptions]
  );

  const mainResinOptions = useMemo(() => {
    const withoutRn1 = resinOptions.filter((resin) => resin.id !== rn1Resin?.id);
    return withoutRn1.length ? withoutRn1 : resinOptions;
  }, [resinOptions, rn1Resin?.id]);

  useEffect(() => {
    if (!fiberOptions.length) return;
    const selectedExists = fiberOptions.some((fiber) => Number(fiber.id) === Number(selectedFiberId));
    if (!selectedExists) {
      setSelectedFiberId(fiberOptions[0].id);
    }
  }, [fiberOptions, selectedFiberId]);

  useEffect(() => {
    if (!mainResinOptions.length) return;
    const selectedExists = mainResinOptions.some(
      (resin) => Number(resin.id) === Number(selectedResinId)
    );
    if (!selectedExists) {
      setSelectedResinId(mainResinOptions[0].id);
    }
  }, [mainResinOptions, selectedResinId]);

  const selectedFiber = useMemo(
    () => fiberOptions.find((fiber) => Number(fiber.id) === Number(selectedFiberId)) || null,
    [fiberOptions, selectedFiberId]
  );
  const selectedResin = useMemo(
    () => resinOptions.find((resin) => Number(resin.id) === Number(selectedResinId)) || null,
    [resinOptions, selectedResinId]
  );

  const canIncludeRn1 = !!rn1Resin && Number(selectedResin?.id) !== Number(rn1Resin.id);

  useEffect(() => {
    if (!canIncludeRn1 && includeRn1) {
      setIncludeRn1(false);
    }
  }, [canIncludeRn1, includeRn1]);

  const selectedOptionalRn1 = includeRn1 && canIncludeRn1 ? rn1Resin : null;

  const basePrice =
    parseNumber(selectedFiber?.base_price) +
    parseNumber(selectedResin?.base_price) +
    parseNumber(selectedOptionalRn1?.base_price);
  const finalPrice =
    parseNumber(selectedFiber?.final_price, parseNumber(selectedFiber?.base_price)) +
    parseNumber(selectedResin?.final_price, parseNumber(selectedResin?.base_price)) +
    parseNumber(
      selectedOptionalRn1?.final_price,
      parseNumber(selectedOptionalRn1?.base_price)
    );
  const savings = Math.max(0, basePrice - finalPrice);
  const discountPercent = basePrice > 0 ? Math.round((savings / basePrice) * 100) : 0;

  const imageGallery = useMemo(() => {
    const images = [
      normalizeString(selectedFiber?.image_url),
      normalizeString(selectedResin?.image_url),
      normalizeString(selectedOptionalRn1?.image_url),
    ].filter(Boolean);
    return images.length ? images : ["/ecommerce/FibraResinaForrar.png"];
  }, [selectedFiber?.image_url, selectedResin?.image_url, selectedOptionalRn1?.image_url]);

  useEffect(() => {
    setActiveImage(imageGallery[0] || "/ecommerce/FibraResinaForrar.png");
  }, [imageGallery]);

  const type = useMemo(() => {
    const value = normalizeString(selectedFiber?.type)?.toLowerCase() || null;
    if (value) return value;
    const normalizedName = normalizeString(selectedFiber?.name)?.toLowerCase() || "";
    if (normalizedName.includes("forjada")) return "forjada";
    return null;
  }, [selectedFiber?.name, selectedFiber?.type]);
  const isForjada = type === "forjada";
  const fiberSelectorUnit = isForjada ? "1 kg" : "1 m²";
  const titlePrefix = isForjada ? "KIT KILO FIBRA DE" : "KIT METRO FIBRA DE";
  const kitCostNote = useMemo(() => {
    if (isForjada) {
      return "Costo por kg";
    }

    const unitM2Value = normalizeString(selectedFiber?.unit_m2_cm);
    if (!unitM2Value) {
      return "Costo por kit (1 m² de fibra + 1 kg de resina)";
    }

    const formatted = /cm/i.test(unitM2Value) ? unitM2Value : `${unitM2Value} cm`;
    return `Costo por kit (1 m² de fibra + 1 kg de resina). Unidad m² = (${formatted})`;
  }, [isForjada, selectedFiber?.unit_m2_cm]);
  const detailColumns = useMemo(() => {
    const columns = [];
    const widthValue = normalizeString(selectedFiber?.width_meters);
    const unitValue = normalizeString(selectedFiber?.unit_m2_cm);
    const weightValue = normalizeString(selectedFiber?.weight);
    const thicknessValue = normalizeString(selectedFiber?.thickness);
    const kilosValue = normalizeString(selectedFiber?.kilos);

    if (type === "tejido") {
      if (widthValue) columns.push({ label: "Ancho", value: widthValue });
    } else if (type === "forjada") {
      if (thicknessValue) columns.push({ label: "Grosor", value: thicknessValue });
    } else {
      if (widthValue) columns.push({ label: "Ancho", value: widthValue });
      if (thicknessValue) columns.push({ label: "Grosor", value: thicknessValue });
    }

    if (unitValue && type !== "forjada") columns.push({ label: "Unidad m²", value: unitValue });
    if (weightValue) columns.push({ label: "Peso", value: weightValue });
    if (kilosValue) columns.push({ label: "Unidad kg", value: kilosValue });
    return columns;
  }, [
    selectedFiber?.kilos,
    selectedFiber?.thickness,
    selectedFiber?.unit_m2_cm,
    selectedFiber?.weight,
    selectedFiber?.width_meters,
    type,
  ]);

  const deliveryRangeLabel = useMemo(() => {
    const now = new Date();
    const start = new Date(now);
    const end = new Date(now);
    start.setDate(now.getDate() + 5);
    end.setDate(now.getDate() + 7);
    const formatter = new Intl.DateTimeFormat("es-MX", { month: "short", day: "numeric" });
    return `${formatter.format(start)} y ${formatter.format(end)}`;
  }, []);

  const description =
    normalizeString(selectedFiber?.description) || "[Aquí debe ir la descripción del producto.]";
  const presentations =
    normalizeString(selectedFiber?.presentations) || "[Aquí deben ir las presentaciones.]";
  const compatibility =
    normalizeString(selectedFiber?.compatibility) || "[Aquí debe ir la compatibilidad de procesos.]";
  const technicalSheetUrl = normalizeString(selectedFiber?.technical_sheet_url);

  const canAddToCart = !!selectedFiber && !!selectedResin;

  const addCurrentKit = () => {
    if (!canAddToCart) return;

    const fiberName = selectedFiber?.name || "Fibra";
    const resinName = selectedResin?.name || "Resina";
    const rn1Name = selectedOptionalRn1?.name ? ` + ${selectedOptionalRn1.name}` : "";

    const fiberKitUnit = isForjada ? "1kg" : "1m²";

    addItem({
      type: "medio-mayorista-kit-fibra-resina",
      name: `KIT ${fiberKitUnit} + 1kg: ${fiberName} + ${resinName}${rn1Name}`,
      unitPrice: finalPrice,
      quantity,
      unitEquivalent: selectedOptionalRn1 ? 3 : 2,
      payload: {
        fiber_id: selectedFiber.id,
        resin_id: selectedResin.id,
        ...(selectedOptionalRn1 ? { rn1_resin_id: selectedOptionalRn1.id } : {}),
      },
    });
    openCart();
  };

  if (loading) {
    return (
      <section className="min-h-screen bg-white px-4 py-8 md:px-8">
        <div className="mx-auto max-w-[1180px] rounded-xl border border-gray-200 p-6 text-center text-gray-600">
          Cargando kit de fibra y resina...
        </div>
      </section>
    );
  }

  if (error || !selectedFiber || !selectedResin) {
    return (
      <section className="min-h-screen bg-white px-4 py-8 md:px-8">
        <div className="mx-auto max-w-[1180px] rounded-xl border border-red-300 bg-red-50 p-6 text-center text-red-700">
          {error || "No se pudo configurar el kit."}
        </div>
      </section>
    );
  }

  return (
    <section className="relative min-h-screen bg-white px-4 py-6 md:px-8 md:py-8">
      <div className="fixed right-3 md:right-8 top-[96px] md:top-[104px] z-30">
        <button type="button" onClick={openCart} className="relative rounded-full">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-green shadow-[0_10px_22px_rgba(0,0,0,0.35)] md:h-16 md:w-16">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-7 w-7 text-white md:h-9 md:w-9"
            >
              <circle cx="9" cy="20" r="1.6" />
              <circle cx="18" cy="20" r="1.6" />
              <path d="M2 2h2l2.4 11.5a2 2 0 0 0 2 1.6h8.8a2 2 0 0 0 2-1.6L21 6H7" />
            </svg>
          </span>
          {!!items.length && (
            <span className="absolute -top-2 -right-2 flex h-7 min-w-[28px] items-center justify-center rounded-full bg-[#ff3030] px-1.5 text-sm font-bold text-white">
              {items.length}
            </span>
          )}
        </button>
      </div>

      <div className="mx-auto max-w-[1180px] text-left">
        <div className="mt-4 grid grid-cols-1 gap-7 md:grid-cols-[470px,1fr] md:gap-10">
          <div className="md:col-span-2">
            <div className="w-full md:w-[470px]">
              <Link
                to="/productos/FibraDeCarbono"
                className="mb-1 inline-flex items-center gap-2 text-[#6f7781] hover:text-[#4f5560]"
              >
                <img src={backIcon} alt="Volver" className="h-9 w-9 opacity-95" />
                <span className="text-lg font-extrabold uppercase tracking-wide">
                  Volver a "Fibra de Carbono"
                </span>
              </Link>
            </div>
          </div>

          <div>
            <div className="relative overflow-hidden rounded-md border border-gray-300 bg-white">
              {discountPercent > 0 && (
                <div className="absolute left-2 top-2 z-10 rounded-full bg-[#e71318] px-2.5 py-1 leading-none text-white shadow-lg">
                  <span className="text-[22px] font-extrabold">-{discountPercent}</span>
                  <span className="pl-0.5 align-top text-xs font-bold">%</span>
                  <span className="block -mt-0.5 text-[13px] font-extrabold uppercase">off</span>
                </div>
              )}

              <img
                src={activeImage || "/ecommerce/FibraResinaForrar.png"}
                alt="Kit fibra y resina"
                className="h-[300px] w-full object-cover md:h-[480px]"
                onError={(event) => {
                  event.currentTarget.onerror = null;
                  event.currentTarget.src = "/ecommerce/FibraResinaForrar.png";
                }}
              />
            </div>

            <div className="mt-3 flex gap-3">
              {imageGallery.slice(0, 4).map((imgSrc, index) => {
                const isActive = activeImage === imgSrc;
                return (
                  <button
                    key={`${imgSrc}-${index}`}
                    type="button"
                    onClick={() => setActiveImage(imgSrc)}
                    className={`h-16 w-16 overflow-hidden rounded border ${
                      isActive ? "border-black" : "border-gray-300"
                    }`}
                  >
                    <img
                      src={imgSrc}
                      alt={`Vista ${index + 1}`}
                      className="h-full w-full object-cover"
                    />
                  </button>
                );
              })}
            </div>
          </div>

          <div className="pb-8 text-left">
            <h1 className="text-3xl font-extrabold uppercase leading-[0.95] text-black md:text-5xl">
              {titlePrefix}
              <br />
              CARBONO + KILO RESINA
            </h1>
            <h2 className="mt-1 text-xl uppercase tracking-tight text-[#111827] md:text-3xl">
              OMOVA CARBON FIBER
            </h2>

            <div className="mt-5 space-y-4">
              <div>
                <p className="text-base font-semibold text-[#111827] md:text-lg">
                  1. Elige el tipo de fibra de carbono ({fiberSelectorUnit}):
                </p>
                <select
                  value={selectedFiberId ?? ""}
                  onChange={(event) => setSelectedFiberId(Number(event.target.value))}
                  className="mt-2 w-full max-w-[420px] border border-[#bdbdbd] bg-white px-3 py-2 text-base font-bold text-[#222]"
                >
                  {fiberOptions.map((fiber) => (
                    <option key={fiber.id} value={fiber.id}>
                      {(() => {
                        const fiberName = fiber.name;
                        const fiberType = normalizeString(fiber?.type)?.toLowerCase() || "";
                        const fiberLooksForjada =
                          fiberType === "forjada" ||
                          (!fiberType &&
                            (normalizeString(fiber?.name)?.toLowerCase() || "").includes("forjada"));

                        if (fiberLooksForjada) {
                          const kilos = normalizeString(fiber?.kilos);
                          return kilos ? `${fiberName} (${kilos})` : fiberName;
                        }

                        const unit = normalizeString(fiber?.unit_m2_cm);
                        return unit ? `${fiberName} (m² = ${unit})` : fiberName;
                      })()}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <p className="text-base font-semibold text-[#111827] md:text-lg">
                  2. Elige el tipo de resina (1 kg):
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {mainResinOptions.map((resin) => (
                    <button
                      key={resin.id}
                      type="button"
                      onClick={() => setSelectedResinId(Number(resin.id))}
                      className={`relative min-w-[96px] rounded px-4 py-2 text-sm font-extrabold uppercase tracking-wide leading-tight transition ${
                        Number(selectedResinId) === Number(resin.id)
                          ? "bg-black text-white"
                          : "border border-[#bdbdbd] bg-white text-[#111827] hover:bg-[#f4f4f4]"
                      }`}
                    >
                      {isRecommendedResin(resin.name) && (
                        <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded bg-[#e71318] px-2 py-0.5 text-[10px] font-bold uppercase leading-none text-white">
                          Recomendada
                        </span>
                      )}
                      {resin.name}
                    </button>
                  ))}
                  {rn1Resin && (
                    <>
                      <span className="ml-2 text-base text-[#222]">Opcional:</span>
                      <button
                        type="button"
                        onClick={() => {
                          if (!canIncludeRn1) return;
                          setIncludeRn1((prev) => !prev);
                        }}
                        disabled={!canIncludeRn1}
                        className={`min-w-[96px] rounded border px-4 py-2 text-sm font-extrabold uppercase tracking-wide leading-tight transition ${
                          includeRn1
                            ? "border-black bg-black text-white"
                            : "border-[#bdbdbd] bg-white text-[#111827] hover:bg-[#f4f4f4]"
                        } disabled:cursor-not-allowed disabled:opacity-50`}
                      >
                        Negra
                        <br />
                        RN1
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>

            {savings > 0 && (
              <div className="mt-4 w-fit bg-[#e71318] px-4 py-1 text-xl font-extrabold leading-none text-white md:text-2xl">
                Ahorra {formatCurrency.format(savings)}
              </div>
            )}

            <div className="mt-3 flex flex-wrap items-end gap-x-3 gap-y-1">
              <span className="text-4xl font-extrabold leading-none text-[#df1414] md:text-5xl">
                {formatCurrency.format(finalPrice)}
                <sup className="text-lg md:text-xl">*</sup>
              </span>
              {basePrice > finalPrice && (
                <span className="text-2xl leading-none text-[#b9b9b9] line-through md:text-3xl">
                  {formatCurrency.format(basePrice)}
                </span>
              )}
            </div>

            <div className="mt-4 flex flex-nowrap items-center gap-3">
              <div className="inline-flex overflow-hidden rounded border border-[#cccccc] bg-[#f2f2f2] text-[#222]">
                <button
                  type="button"
                  onClick={() => setQuantity((prev) => Math.max(1, prev - 1))}
                  className="px-4 py-1 text-xl leading-none hover:bg-[#e7e7e7]"
                >
                  -
                </button>
                <span className="px-5 py-1 text-xl leading-none">{quantity}</span>
                <button
                  type="button"
                  onClick={() => setQuantity((prev) => prev + 1)}
                  className="px-4 py-1 text-xl leading-none hover:bg-[#e7e7e7]"
                >
                  +
                </button>
              </div>

              <button
                type="button"
                onClick={addCurrentKit}
                disabled={!canAddToCart}
                className="whitespace-nowrap rounded bg-primary-green px-5 py-2 text-base font-bold uppercase leading-none tracking-wide text-white transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-50 md:text-lg"
              >
                Agregar al carrito
              </button>
            </div>

            <div className="mt-4 space-y-1 text-sm text-[#333] md:text-base">
              <p>IVA incluido. El costo de envío se calcula en el carrito.</p>
              <p className="font-semibold">*{kitCostNote}</p>
            </div>

            <div className="mt-2 inline-flex items-center gap-3 bg-black px-3 py-1.5 text-sm font-semibold text-white md:text-base">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-5 w-5 shrink-0"
              >
                <rect x="1" y="3" width="15" height="13" />
                <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
                <circle cx="5.5" cy="18.5" r="1.5" />
                <circle cx="18.5" cy="18.5" r="1.5" />
              </svg>
              <span>Tu pedido llegaría entre {deliveryRangeLabel}</span>
            </div>

            <p className="mt-4 text-base text-gray-500 md:text-2xl">
              <Link to="/form" className="font-semibold text-primary-green underline">
                Regístrate
              </Link>{" "}
              para obtener acceso a este y más productos en nuestra plataforma mayorista
            </p>

            <div className="mt-6">
              <h3 className="text-2xl font-extrabold text-[#111827] md:text-4xl">
                Información técnica:
              </h3>

              <p className="mt-2 text-lg leading-tight text-[#111827] md:text-2xl">{description}</p>

              {detailColumns.length > 0 && (
                <div className="mt-3 overflow-hidden rounded border border-black">
                  <table className="w-full border-collapse text-center text-sm md:text-lg">
                    <thead>
                      <tr>
                        {detailColumns.map((column) => (
                          <th key={column.label} className="border border-black px-4 py-1 font-bold">
                            {column.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        {detailColumns.map((column) => (
                          <td
                            key={`${column.label}-${column.value}`}
                            className="border border-black px-4 py-1"
                          >
                            {column.value}
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              <p className="mt-3 text-lg leading-tight text-[#111827] md:text-2xl">
                <span className="font-bold">Presentaciones:</span> {presentations}
              </p>
              <p className="mt-3 text-base leading-tight text-[#111827] md:text-xl">
                <span className="font-bold">Compatibilidad de procesos:</span> {compatibility}
              </p>
            </div>

            {technicalSheetUrl ? (
              <a
                href={technicalSheetUrl}
                target="_blank"
                rel="noreferrer"
                className="mt-4 inline-flex rounded-md bg-black px-5 py-2 text-lg font-bold text-white hover:bg-gray-900 md:text-xl"
              >
                Descargar ficha técnica
              </a>
            ) : (
              <button
                type="button"
                disabled
                className="mt-4 inline-flex cursor-not-allowed rounded-md bg-black/80 px-5 py-2 text-lg font-bold text-white md:text-xl"
              >
                Descargar ficha técnica
              </button>
            )}
          </div>
        </div>
      </div>
      <OtherProductsCarousel items={otherProducts} />
    </section>
  );
};

export default MedioMayoristaKitFiberResinDetail;
